const TermsPage = () => {
  return (
    <div className="pt-20" data-testid="terms-page">
      {/* Hero Section */}
      <section className="bg-forest-950 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white mb-4">
              Terms & Conditions
            </h1>
            <p className="text-emerald-100/80 text-lg">
              Last updated: December 2024
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <div className="space-y-8 text-gray-700">
              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">1. Agreement to Terms</h2>
                <p>
                  By accessing or using the services of Nexivas Lawn Care ("Company," "we," "us," or "our"), you agree to be bound by these Terms and Conditions ("Terms"). If you disagree with any part of these terms, you may not access our services.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">2. Services</h2>
                <p>
                  Nexivas Lawn Care provides professional lawn care and landscaping services including, but not limited to:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Lawn mowing and maintenance</li>
                  <li>Weed control and fertilization</li>
                  <li>Yard cleanup and debris removal</li>
                  <li>Landscaping and garden maintenance</li>
                  <li>Seasonal services</li>
                </ul>
                <p className="mt-4">
                  All services are subject to availability and may vary by location. We reserve the right to modify or discontinue any service at any time.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">3. Quotes and Pricing</h2>
                <p>
                  All quotes provided are estimates based on the information available at the time of assessment. Final pricing may vary based on:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Actual property conditions</li>
                  <li>Additional services requested</li>
                  <li>Unforeseen circumstances or obstacles</li>
                  <li>Changes in material costs</li>
                </ul>
                <p className="mt-4">
                  Quotes are valid for 30 days from the date of issue unless otherwise specified.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">4. Scheduling and Service</h2>
                <p>
                  <strong>Service Schedule:</strong> Services will be performed on a mutually agreed schedule. We will make reasonable efforts to maintain consistent service days, but schedules may be adjusted due to weather conditions, holidays, or operational requirements.
                </p>
                <p className="mt-4">
                  <strong>Weather Conditions:</strong> Services may be rescheduled due to inclement weather, including rain, extreme heat, or other conditions that may affect service quality or safety.
                </p>
                <p className="mt-4">
                  <strong>Access:</strong> You must provide reasonable access to your property for service completion. This includes unlocking gates, removing obstacles, and securing pets if applicable.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">5. Payment Terms</h2>
                <p>
                  <strong>Invoicing:</strong> Services will be invoiced upon completion. Payment is due within 15 days of the invoice date unless otherwise agreed.
                </p>
                <p className="mt-4">
                  <strong>Late Payments:</strong> A late fee of 1.5% per month may be applied to overdue balances. Continued non-payment may result in suspension of services.
                </p>
                <p className="mt-4">
                  <strong>Accepted Payment Methods:</strong> We accept payment via check, credit card, debit card, and electronic transfer.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">6. Cancellation Policy</h2>
                <p>
                  <strong>Service Cancellation:</strong> You may cancel a scheduled service with at least 24 hours notice without penalty. Cancellations with less than 24 hours notice may be subject to a cancellation fee.
                </p>
                <p className="mt-4">
                  <strong>Contract Termination:</strong> Either party may terminate an ongoing service agreement with 30 days written notice. Any prepaid services will be refunded on a prorated basis.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">7. Satisfaction Guarantee</h2>
                <p>
                  We stand behind the quality of our work. If you are not satisfied with our service, please contact us within 48 hours of service completion. We will work with you to address any concerns and, if necessary, will re-service your property at no additional cost.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">8. Liability and Insurance</h2>
                <p>
                  <strong>Insurance:</strong> Nexivas Lawn Care maintains general liability insurance and workers' compensation coverage for all employees.
                </p>
                <p className="mt-4">
                  <strong>Limitation of Liability:</strong> Our liability for any damages is limited to the cost of the services provided. We are not liable for pre-existing conditions, damage from hidden objects (sprinkler heads, utilities, etc.), or damage resulting from adverse weather conditions.
                </p>
                <p className="mt-4">
                  <strong>Property Disclosure:</strong> You are responsible for disclosing the location of any underground utilities, irrigation systems, or other hazards prior to service.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">9. Indemnification</h2>
                <p>
                  You agree to indemnify and hold harmless Nexivas Lawn Care, its officers, employees, and agents from any claims, damages, or expenses arising from:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Your violation of these Terms</li>
                  <li>Your misuse of our services</li>
                  <li>Your failure to disclose property hazards</li>
                  <li>Any third-party claims related to services performed on your property</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">10. Intellectual Property</h2>
                <p>
                  All content on our website, including text, graphics, logos, and images, is the property of Nexivas Lawn Care and is protected by copyright and trademark laws. You may not reproduce, distribute, or use our content without written permission.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">11. Website Use</h2>
                <p>
                  When using our website, you agree not to:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Use the site for any unlawful purpose</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Interfere with the proper functioning of the website</li>
                  <li>Submit false or misleading information</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">12. Dispute Resolution</h2>
                <p>
                  Any disputes arising from these Terms or our services shall be resolved through:
                </p>
                <ol className="list-decimal pl-6 mt-3 space-y-2">
                  <li><strong>Informal Resolution:</strong> We will attempt to resolve any dispute informally through direct communication.</li>
                  <li><strong>Mediation:</strong> If informal resolution fails, disputes will be submitted to mediation before proceeding to arbitration or litigation.</li>
                  <li><strong>Governing Law:</strong> These Terms shall be governed by the laws of the State of Texas.</li>
                </ol>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">13. Changes to Terms</h2>
                <p>
                  We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after changes constitutes acceptance of the modified Terms.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">14. Severability</h2>
                <p>
                  If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">15. Contact Information</h2>
                <p>If you have questions about these Terms and Conditions, please contact us:</p>
                <div className="mt-4 bg-emerald-50 rounded-xl p-6">
                  <p><strong>Nexivas Lawn Care</strong></p>
                  <p>667 Magnolia Court</p>
                  <p>Houston, TX 57002</p>
                  <p className="mt-3">
                    <strong>Email:</strong> <a href="mailto:support@nexivas.store" className="text-emerald-600 hover:text-emerald-700">support@nexivas.store</a>
                  </p>
                  <p>
                    <strong>Phone:</strong> <a href="tel:+1-771-1871-987" className="text-emerald-600 hover:text-emerald-700">+1-771-1871-987</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
